import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSignUp, useClerk } from '@clerk/clerk-react';
import { useAuth } from '../App';
import { OTPInput } from '../components/auth/OTPInput';
import { ClerkStatusNotice } from '../components/auth/ClerkStatusNotice';
import { authErrorMessage, clerkErrorCode, exchangeClerkSession } from '../lib/clerkAuth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, User, Check, X, Loader2, ArrowLeft, RefreshCw } from 'lucide-react';
import GoogleButton from '../components/auth/GoogleButton';

const PASSWORD_RULES = [
  { test: (p) => p.length >= 8, label: 'At least 8 characters' },
  { test: (p) => /[A-Z]/.test(p), label: 'One uppercase letter' },
  { test: (p) => /[a-z]/.test(p), label: 'One lowercase letter' },
  { test: (p) => /[0-9]/.test(p), label: 'One number' },
  { test: (p) => /[!@#$%^&*(),.?":{}|<>\-_=+\[\]\\;'`~]/.test(p), label: 'One special character' },
];

export default function Register() {
  const [searchParams] = useSearchParams();
  const defaultRole = searchParams.get('role') || 'client';

  const [step, setStep] = useState('form'); // 'form' | 'verify'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(defaultRole);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // OTP state
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const { isLoaded, signUp, setActive } = useSignUp();
  const clerk = useClerk();

  const passwordChecks = useMemo(() => PASSWORD_RULES.map(rule => ({
    ...rule,
    passed: rule.test(password),
  })), [password]);

  const allPasswordChecksPassed = passwordChecks.every(c => c.passed);

  // Cooldown timer for resend
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleSendVerification = async (e) => {
    e.preventDefault();
    if (!allPasswordChecksPassed) {
      toast.error('Please meet all password requirements');
      return;
    }
    if (!isLoaded) return;
    setLoading(true);
    try {
      const [firstName, ...rest] = name.trim().split(/\s+/);
      await signUp.create({ emailAddress: email.trim(), password, firstName, lastName: rest.join(' ') || undefined });
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setStep('verify');
      setCooldown(60);
      toast.success('Verification code sent to your email');
    } catch (error) {
      if (clerkErrorCode(error) === 'form_identifier_exists') {
        toast.error('An account with this email already exists. Please sign in instead.');
      } else {
        toast.error(authErrorMessage(error, 'Failed to send verification code'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setVerifying(true);
    try {
      const result = await signUp.attemptEmailAddressVerification({ code: otp });
      if (result.status !== 'complete') throw new Error('Sign-up needs another step. Please try again.');
      await setActive({ session: result.createdSessionId });
      const data = await exchangeClerkSession(clerk, {
        name: name.trim(), role,
        signup_source: searchParams.get('from') || null,
        signup_pillar: searchParams.get('category') || null,
      });
      try { await checkAuth(); } catch (err) { console.error('Auth check after verify:', err); }
      toast.success('Account created successfully!');
      if (data.role === 'provider') {
        // Forward funnel context so /for-barbers pre-ticks the right pillar in onboarding.
        const passthroughCategory = searchParams.get('category');
        navigate(passthroughCategory
          ? `/provider/onboarding?category=${encodeURIComponent(passthroughCategory)}`
          : '/provider/onboarding', { replace: true });
      } else {
        navigate('/discover', { replace: true });
      }
    } catch (error) {
      toast.error(authErrorMessage(error, 'Invalid code'));
      setOtp('');
      // Expired or locked code: let them request a fresh one straight away.
      if (clerkErrorCode(error) && clerkErrorCode(error) !== 'form_code_incorrect') setCooldown(0);
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || !isLoaded) return;
    setResending(true);
    try {
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setCooldown(60);
      setOtp('');
      toast.success('New code sent');
    } catch (error) {
      toast.error(authErrorMessage(error, 'Failed to resend code'));
    } finally {
      setResending(false);
    }
  };

  // Verification step
  if (step === 'verify') {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <h1 className="font-heading text-4xl font-medium text-[#1A1A1A]">Zuriya</h1>
            </Link>
            <p className="text-stone-500 mt-2">Verify your email</p>
          </div>

          <div className="card-surface p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-[#F9F6F0] border-2 border-[#D4AF37] flex items-center justify-center mx-auto mb-4">
                <Mail className="h-7 w-7 text-[#D4AF37]" />
              </div>
              <h2 className="font-heading text-xl font-medium text-[#1A1A1A] mb-1" data-testid="verify-title">
                Check your inbox
              </h2>
              <p className="text-sm text-stone-500">
                We've sent a 6-digit code to<br />
                <span className="font-medium text-[#1A1A1A]">{email}</span>
              </p>
            </div>

            <div className="space-y-6">
              <OTPInput value={otp} onChange={setOtp} disabled={verifying} />

              <Button
                onClick={handleVerify}
                disabled={otp.length !== 6 || verifying}
                className="w-full btn-primary h-12"
                data-testid="verify-submit"
              >
                {verifying ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" />Verifying...</>
                ) : (
                  'Verify & Create Account'
                )}
              </Button>

              <div className="text-center space-y-3">
                <p className="text-sm text-stone-500">
                  Didn't receive the code?{' '}
                  {cooldown > 0 ? (
                    <span className="text-stone-400">Resend in {cooldown}s</span>
                  ) : (
                    <button
                      onClick={handleResend}
                      disabled={resending}
                      className="text-[#D4AF37] hover:underline font-medium inline-flex items-center gap-1"
                      data-testid="resend-code-btn"
                    >
                      {resending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                      Resend code
                    </button>
                  )}
                </p>
                <button
                  onClick={() => { setStep('form'); setOtp(''); }}
                  className="text-sm text-stone-400 hover:text-stone-600 inline-flex items-center gap-1"
                  data-testid="back-to-form-btn"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Change email or details
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Registration form step
  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="font-heading text-4xl font-medium text-[#1A1A1A]">Zuriya</h1>
          </Link>
          <p className="text-stone-500 mt-2">Create your account</p>
        </div>

        <div className="card-surface p-8">
          <ClerkStatusNotice />
          <form onSubmit={handleSendVerification} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-[#1A1A1A]">Full Name</Label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name"
                  className="input-zuriya pl-10"
                  required
                  data-testid="register-name"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-[#1A1A1A]">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="input-zuriya pl-10"
                  required
                  data-testid="register-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-[#1A1A1A]">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a strong password"
                  className="input-zuriya pl-10 pr-10"
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {password && (
                <div className="mt-3 space-y-1.5 animate-fadeIn" data-testid="password-hints">
                  {passwordChecks.map((check) => (
                    <div key={check.label} className={`flex items-center gap-2 text-xs transition-colors ${check.passed ? 'text-green-600' : 'text-stone-400'}`}>
                      {check.passed ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                      {check.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium text-[#1A1A1A] mb-3 block">I want to</Label>
              <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 gap-3">
                <Label
                  htmlFor="role-client"
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    role === 'client' ? 'border-[#D4AF37] bg-[#F9F6F0]' : 'border-[#E6E4DD] hover:border-[#D4AF37]/50'
                  }`}
                >
                  <RadioGroupItem value="client" id="role-client" />
                  <div>
                    <p className="font-medium text-sm text-[#1A1A1A]">Book Services</p>
                    <p className="text-xs text-stone-400">Find & book beauty</p>
                  </div>
                </Label>
                <Label
                  htmlFor="role-provider"
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    role === 'provider' ? 'border-[#D4AF37] bg-[#F9F6F0]' : 'border-[#E6E4DD] hover:border-[#D4AF37]/50'
                  }`}
                >
                  <RadioGroupItem value="provider" id="role-provider" />
                  <div>
                    <p className="font-medium text-sm text-[#1A1A1A]">Offer Services</p>
                    <p className="text-xs text-stone-400">List your business</p>
                  </div>
                </Label>
              </RadioGroup>
            </div>

            <div id="clerk-captcha" data-testid="clerk-captcha" />

            <Button 
              type="submit" 
              className="w-full btn-primary h-12" 
              disabled={loading || !allPasswordChecksPassed || !isLoaded}
              data-testid="register-submit"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sending Code...</>
              ) : (
                'Continue'
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#E6E4DD]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-stone-500">or</span>
            </div>
          </div>

          <GoogleButton role={role} />

          <div className="mt-6 text-center text-sm text-stone-500">
            Already have an account?{' '}
            <Link to="/login" className="text-[#D4AF37] hover:underline font-medium" data-testid="login-link">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
